import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const PATHNAME = '/search';

const SEARCH_QUERY = 'q';

const CATEGORY_QUERY = 'results';

const CATEGORIES = ['Products', 'Projects', 'Tutorials', 'All'];

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @property {HTMLElement} searchPageSuggestions - The search suggestions element.
 * @property {HTMLTemplateElement} searchPageSuggestionItem
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput','searchPageSuggestions'];

  #siteId = ""

  connectedCallback() {
    super.connectedCallback();

    this.#siteId = this.getAttribute('data-site-id') ?? '';
  }

  /**
   * Value from the input field
   */
  get searchInputValue() {
    return this.refs.searchPageInput.value.trim();
  }

  /**
   * Handles the click event on the clear button and submits an empty search.
   * This clears the search input and resubmits the form if the page is not
   * already in an empty state.
   */
  handleClearClick() {
    this.#submitEmptySearch();
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce(async (event) => {
    const value = this.searchInputValue;

    if (value != '') {
      await this.#populateSuggestions(value);
    }

    this.#setSuggestionsDisplay(value);

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  /**
   * Display the suggestions when the input is focused and has content
   */
  handleFocus = () => {
    // delay a beat before showing the suggestions, keeps from overreacting
    setTimeout(async () => {
      const { searchInputValue } = this;
      await this.#populateSuggestions(searchInputValue);
      this.#setSuggestionsDisplay(searchInputValue);
    }, 150);
  }

  /**
   * Remove the suggestions when the input loses focus
   */
  handleBlur = () => {
    // delay hiding the suggestions to allow for click events
    setTimeout(() => {
      this.#setSuggestionsDisplay(this.searchInputValue);
    }, 150);
  }

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';
    this.#setSuggestionsDisplay('');

    searchInput.form?.submit();
  }

  /**
   * Show/hide the suggestion dropdown
   *
   * @param {string} value - Value from the input
   */
  #setSuggestionsDisplay(value) {
    const focused = this.refs.searchPageInput == document.activeElement;
    const hidden = !focused || value == '' ? 'true' : 'false'
    const suggestions = this.refs.searchPageSuggestions;
    suggestions.setAttribute('aria-hidden', hidden);
  }

  /**
   * Make the api call and populate the suggestion drop down with the result
   *
   * @param {string} value - Value from the input
   */
  async #populateSuggestions(value) {
    const suggestions = await this.#getSuggestions(value);
    const suggestedValue = suggestions?.suggested?.text ?? value;

    const template = this.refs.searchPageSuggestionItem;
    const container = this.refs.searchPageSuggestions;
    const fragment = document.createDocumentFragment();
    for(let category of CATEGORIES) {
      const node = template.content.firstElementChild?.cloneNode(true);
      if (!(node instanceof Element)) continue;
      const url = node.querySelector('[data-bind="suggestion:url"]');
      if (url instanceof HTMLAnchorElement) {
        url.href = this.#getUrl(suggestedValue, category);
      }
      const query = node.querySelector('[data-bind="suggestion-query:text"]');
      if (query) {
        query.textContent = `${suggestedValue}: `;
      }
      const cat = node.querySelector('[data-bind="suggestion-category:text"]');
      if (cat) {
        cat.textContent = category;
      }
      fragment.append(node);
    }
    container.replaceChildren(fragment);
  }

  /**
   * Helper to build the proper search url
   *
   * @param {string} query
   * @param {string} category
   */
  #getUrl(query, category) {
    const url = window.location.origin + PATHNAME;
    const params = new URLSearchParams([
      [SEARCH_QUERY, query],
      [CATEGORY_QUERY, category.toLowerCase()],
    ]);
    return url + `?${params.toString()}`;
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };

  /**
   * Make the actual api call for suggestions
   *
   * @param {string} value - Value from the input
   */
  async #getSuggestions(value) {
    const searchParams = new URLSearchParams();
    searchParams.append('siteId', this.#siteId);
    searchParams.append(SEARCH_QUERY, value);
    searchParams.append('disableSpellCorrect', 'true');
    searchParams.append('lang', 'en');
    searchParams.append('limit', '4');
    const res = await fetch(`https://${this.#siteId}.a.searchspring.io/api/suggest/query?${searchParams.toString()}`, {
      method: 'GET',
      headers: {accept: 'application/json'},
    });
    if (!res.ok) {
      // todo: error handling
      throw new Error('Suggestion request failed.');
    }
    return res.json();
  }
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
